import React, { useState, useEffect } from 'react';
import { Search, ChevronDown, Download, X } from 'lucide-react';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

export default function KardexFinalPeriodo() {
    const [filters, setFilters] = useState({
        id_carrera: '',
        id_mencion: '',
        id_asignatura: '',
        id_paralelo: '',
        periodo: '1',
        gestion: new Date().getFullYear()
    });

    const [carreras, setCarreras] = useState([]);
    const [menciones, setMenciones] = useState([]);
    const [asignaturas, setAsignaturas] = useState([]);
    const [paralelos, setParalelos] = useState([]);
    const [estudiantes, setEstudiantes] = useState([]);
    const [notas, setNotas] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [inscripcionCerrada, setInscripcionCerrada] = useState(false);
    const [mensajeCierre, setMensajeCierre] = useState('');

    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [selectedPeriodos, setSelectedPeriodos] = useState([]);

    // Meses del periodo (1 periodo = 3 meses)
    const pNo = parseInt(filters.periodo || 1);
    const meses = [pNo * 3 - 2, pNo * 3 - 1, pNo * 3];
    const camposMes = ['P', 'T', 'Total'];

    useEffect(() => { 
        loadCarreras(); 
        loadAsignaturas(); 
        verificarPeriodoCargadoNotas();
    }, []);

    useEffect(() => {
        if (filters.id_carrera) {
            loadMenciones(filters.id_carrera);
            loadAsignaturas(filters.id_carrera, null);
        } else {
            setMenciones([]);
            loadAsignaturas(null, null);
        }
    }, [filters.id_carrera]);

    useEffect(() => {
        if (filters.id_mencion) {
            loadAsignaturas(filters.id_carrera, filters.id_mencion);
        }
    }, [filters.id_mencion]);

    useEffect(() => {
        if (filters.id_asignatura) loadParalelos(filters.id_asignatura);
        else setParalelos([]);
    }, [filters.id_asignatura]);

    useEffect(() => {
        if (filters.id_asignatura && filters.id_paralelo) loadEstudiantes();
    }, [filters.id_asignatura, filters.id_paralelo, filters.periodo, filters.gestion]);

    const verificarPeriodoCargadoNotas = async () => {
        try {
            const paramName = 'Cargado de notas Licenciatura > Final Periodo (docente)';
            const response = await fetch(`${API_URL}/direccion/parametrizaciones/verificar/${encodeURIComponent(paramName)}`);
            const data = await response.json();
            
            if (!data.habilitado) {
                setInscripcionCerrada(true);
                setMensajeCierre(data.mensaje || 'El periodo de cargado de notas de examen final se encuentra cerrado.');
            } else {
                setInscripcionCerrada(false);
            }
        } catch (error) {
            console.error('Error verificando periodo:', error);
        }
    };

    const loadCarreras = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/docente/carreras`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            setCarreras(Array.isArray(data.carreras) ? data.carreras : []);
        } catch (e) { console.error('Error:', e); setCarreras([]); }
    };

    const loadMenciones = async (id_carrera) => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/docente/menciones/${id_carrera}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            setMenciones(Array.isArray(data.menciones) ? data.menciones : []);
        } catch (e) { console.error('Error:', e); setMenciones([]); }
    };

    const loadAsignaturas = async (carrera, mencion) => {
        try {
            const token = localStorage.getItem('token');
            const query = new URLSearchParams();
            if (carrera) query.append('carrera', carrera);
            if (mencion) query.append('mencion', mencion);
            query.append('gestion', filters.gestion);
            const res = await fetch(`${API_URL}/docente/asignaturas?${query}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            setAsignaturas(Array.isArray(data.asignaturas) ? data.asignaturas : []);
        } catch (e) { console.error('Error:', e); setAsignaturas([]); }
    };

    const loadParalelos = async (id_asignatura) => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/docente/paralelos/${id_asignatura}?gestion=${filters.gestion}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            setParalelos(Array.isArray(data.paralelos) ? data.paralelos : []);
        } catch (e) { console.error('Error:', e); setParalelos([]); }
    };

    const loadEstudiantes = async () => {
        try {
            setLoading(true);
            const query = new URLSearchParams({
                id_asignatura: filters.id_asignatura,
                id_paralelo: filters.id_paralelo,
                gestion: filters.gestion,
                tipo: 'matriculacion'
            });
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/docente/estudiantes?${query}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            setEstudiantes(data.estudiantes || []);
            await loadNotas();
        } catch (e) { console.error('Error:', e); }
        finally { setLoading(false); }
    };

    const loadNotas = async () => {
        try {
            const token = localStorage.getItem('token');
            const commonParams = {
                id_asignatura: filters.id_asignatura,
                id_paralelo: filters.id_paralelo,
                periodo: filters.periodo,
                gestion: filters.gestion
            };

            const queryP = new URLSearchParams({ ...commonParams, tipo: 'periodo' });
            const resP = await fetch(`${API_URL}/docente/notas-periodo?${queryP}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const dataP = await resP.json();

            const queryPr = new URLSearchParams({ ...commonParams, tipo: 'proceso', meses: meses.join(',') });
            const resPr = await fetch(`${API_URL}/docente/notas-proceso?${queryPr}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const dataPr = await resPr.json();

            const resAs = await fetch(`${API_URL}/docente/asistencias/resumen-periodo?${queryPr}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const dataAs = await resAs.json();

            const map = {};
            (dataAs.resumen || []).forEach(r => {
                map[`${r.id_estudiante}_mes0_Asistencia`] = `${r.porcentaje}%`;
            });

            const procAgg = {};
            (dataPr.notas || []).forEach(n => {
                const key = `${n.id_estudiante}_${n.periodo}`; // En notas_proceso, n.periodo almacena el mes
                if (!procAgg[key]) procAgg[key] = { sumsP: 0, countsP: 0, sumsT: 0, countsT: 0 };
                const agg = procAgg[key];
                
                if (['P1', 'P2', 'P3'].includes(n.campo) && n.valor !== null) {
                    agg.sumsP += parseFloat(n.valor || 0);
                    agg.countsP++;
                } else if (['T1', 'T2'].includes(n.campo) && n.valor !== null) {
                    agg.sumsT += parseFloat(n.valor || 0);
                    agg.countsT++;
                }
            });

            (dataP.notas || []).forEach(n => {
                const periodSuffix = n.mes === 0 ? `_p${n.periodo}` : '';
                map[`${n.id_estudiante}${periodSuffix}_mes${n.mes}_${n.campo}`] = n.valor || '';
            });

            Object.keys(procAgg).forEach(k => {
                const [estId, mesNo] = k.split('_');
                const agg = procAgg[k];
                const promP = agg.countsP > 0 ? Math.round(agg.sumsP / agg.countsP) : 0;
                const promT = agg.countsT > 0 ? Math.round(agg.sumsT / agg.countsT) : 0;
                const total = promP + promT;

                map[`${estId}_mes${mesNo}_P`] = promP;
                map[`${estId}_mes${mesNo}_T`] = promT;
                map[`${estId}_mes${mesNo}_Total`] = total;
            });

            setNotas(map);
        } catch (e) { console.error('Error loadNotas Kardex Periodo:', e); }
    };

    const getNota = (id_estudiante, mes, campo) => {
        return notas[`${id_estudiante}_mes${mes}_${campo}`] || '-';
    };

    const getNotaPeriodoFinal = (id_estudiante, campo) => {
        return notas[`${id_estudiante}_p${filters.periodo}_mes0_${campo}`] || '';
    };

    const handleNotaChange = (id_estudiante, campo, valor) => {
        const numVal = parseFloat(valor);
        let maxVal = 100;
        if (campo === 'P') maxVal = 70;
        else if (campo === 'T') maxVal = 30;

        if (valor !== '' && (isNaN(numVal) || numVal < 0 || numVal > maxVal)) return;
        setNotas(prev => ({ ...prev, [`${id_estudiante}_p${filters.periodo}_mes0_${campo}`]: valor }));
    };

    const handleGuardar = async () => {
        try {
            setSaving(true);
            const gestion = filters.gestion || new Date().getFullYear();
            const entries = [];

            const asignaturaNombre = asignaturas.find(a => String(a.id_asignatura) === String(filters.id_asignatura))?.nombre || 'Asignatura';

            estudiantes.forEach(est => {
                const p = getNotaPeriodoFinal(est.id_estudiante, 'P');
                const t = getNotaPeriodoFinal(est.id_estudiante, 'T');
                
                if (p !== '') {
                    entries.push({
                        id_estudiante: est.id_estudiante,
                        id_asignatura: filters.id_asignatura,
                        id_paralelo: filters.id_paralelo,
                        mes: 0,
                        campo: 'P',
                        valor: parseFloat(p),
                        gestion,
                        periodo: parseInt(filters.periodo)
                    });
                }
                if (t !== '') {
                    entries.push({
                        id_estudiante: est.id_estudiante,
                        id_asignatura: filters.id_asignatura,
                        id_paralelo: filters.id_paralelo,
                        mes: 0,
                        campo: 'T',
                        valor: parseFloat(t),
                        gestion,
                        periodo: parseInt(filters.periodo)
                    });
                }

                const totalFinal = getTotal100(est.id_estudiante);
                if (parseFloat(totalFinal) > 0) {
                    entries.push({
                        id_estudiante: est.id_estudiante,
                        id_asignatura: filters.id_asignatura,
                        id_paralelo: filters.id_paralelo,
                        mes: 0,
                        campo: 'NF',
                        valor: parseFloat(totalFinal),
                        gestion,
                        periodo: parseInt(filters.periodo),
                        nombre_asignatura: asignaturaNombre
                    });
                }
            });

            const token = localStorage.getItem('token');
            await fetch(`${API_URL}/docente/notas-periodo/bulk`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ notas: entries })
            });

            alert('Notas guardadas exitosamente');
        } catch (e) { console.error('Error:', e); alert('Error al guardar'); }
        finally { setSaving(false); }
    };

    const getPromProceso = (id_estudiante) => {
        let sum = 0, count = 0;
        meses.forEach(mes => {
            const total = parseFloat(getNota(id_estudiante, mes, 'Total')) || 0;
            if (total > 0) { sum += total; count++; }
        });
        const avg = count > 0 ? (sum / count) : 0;
        return Math.round(avg * 0.7);
    };

    const getSubTotalExamen = (id_estudiante) => {
        const p = parseFloat(getNotaPeriodoFinal(id_estudiante, 'P') || 0);
        const t = parseFloat(getNotaPeriodoFinal(id_estudiante, 'T') || 0);
        return Math.round((p + t) * 0.3);
    };

    const getTotal100 = (id_estudiante) => {
        const proc = parseFloat(getPromProceso(id_estudiante)) || 0;
        const exam = parseFloat(getSubTotalExamen(id_estudiante)) || 0;
        return Math.round(proc + exam);
    };

    const togglePeriodo = (p) => {
        setSelectedPeriodos(prev => 
            prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]
        );
    };

    const handleDownload = async () => {
        try {
            if (selectedPeriodos.length === 0) {
                alert('Seleccione al menos un periodo para descargar');
                return;
            }

            const token = localStorage.getItem('token');
            const query = new URLSearchParams({ 
                periodo: selectedPeriodos.join(','), 
                tipo: 'periodo',
                id_asignatura: filters.id_asignatura,
                id_paralelo: filters.id_paralelo,
                gestion: filters.gestion
            });
            const res = await fetch(`${API_URL}/docente/notas-periodo/export?${query}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!res.ok) throw new Error('Error al generar Excel');

            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `notas_periodo_${filters.id_asignatura}_${filters.gestion}.xlsx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            setShowDownloadModal(false);
        } catch (e) { alert('Error al descargar'); }
    };

    const filteredEstudiantes = estudiantes.filter(est => {
        const s = searchTerm.toLowerCase();
        return (est.codigo || '').toLowerCase().includes(s) ||
            (est.nombres || '').toLowerCase().includes(s) ||
            (est.apellidos || '').toLowerCase().includes(s);
    });

    return (
        <div className="p-4 md:p-6 max-w-full mx-auto">
            <h1 className="text-xl font-bold text-gray-800 mb-6">Kardex &gt; Calificaciones &gt; Final Periodo</h1>

            <div className="flex flex-wrap gap-2 mb-6">
                <div className="relative">
                    <select value={filters.gestion} onChange={(e) => setFilters({ ...filters, gestion: parseInt(e.target.value) })}
                        className="appearance-none pl-4 pr-10 py-2 border border-gray-300 rounded-full bg-white text-sm min-w-[100px]">
                        {[2024, 2025, 2026, 2027].map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={18} />
                </div>

                <div className="relative">
                    <select value={filters.id_carrera} onChange={(e) => setFilters({ ...filters, id_carrera: e.target.value, id_mencion: '', id_asignatura: '', id_paralelo: '' })}
                        className="appearance-none pl-4 pr-10 py-2 border border-gray-300 rounded-full bg-white text-sm min-w-[150px]">
                        <option value="">Carrera</option>
                        {carreras.map(c => <option key={c.id_carrera} value={c.id_carrera}>{c.nombre}</option>)}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={18} />
                </div>

                <div className="relative">
                    <select value={filters.id_mencion} onChange={(e) => setFilters({ ...filters, id_mencion: e.target.value, id_asignatura: '', id_paralelo: '' })}
                        disabled={!filters.id_carrera} className="appearance-none pl-4 pr-10 py-2 border border-gray-300 rounded-full bg-white text-sm min-w-[150px] disabled:bg-gray-50">
                        <option value="">Mención</option>
                        {menciones.map(m => <option key={m.id_mencion} value={m.id_mencion}>{m.nombre}</option>)}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={18} />
                </div>

                <div className="relative">
                    <select value={filters.id_asignatura} onChange={(e) => setFilters({ ...filters, id_asignatura: e.target.value, id_paralelo: '' })}
                        className="appearance-none pl-4 pr-10 py-2 border border-gray-300 rounded-full bg-white text-sm min-w-[200px]">
                        <option value="">Asignatura</option>
                        {asignaturas.map(a => <option key={a.id_asignatura} value={a.id_asignatura}>{a.nombre}</option>)}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={18} />
                </div>

                <div className="relative">
                    <select value={filters.id_paralelo} onChange={(e) => setFilters({ ...filters, id_paralelo: e.target.value })}
                        disabled={!filters.id_asignatura} className="appearance-none pl-4 pr-10 py-2 border border-gray-300 rounded-full bg-white text-sm min-w-[120px] disabled:bg-gray-100">
                        <option value="">Paralelo</option>
                        {paralelos.map(p => <option key={p.id_paralelo || p.paralelo} value={p.id_paralelo || p.paralelo}>{p.paralelo || p.nombre}</option>)}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={18} />
                </div>

                <div className="relative">
                    <select value={filters.periodo} onChange={(e) => setFilters({ ...filters, periodo: e.target.value })}
                        className="appearance-none pl-4 pr-10 py-2 border border-gray-300 rounded-full bg-white text-sm min-w-[100px]">
                        {[1, 2, 3, 4].map(p => <option key={p} value={p}>Periodo {p}</option>)}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={18} />
                </div>

                <div className="relative flex-1 max-w-xs">
                    <input type="text" placeholder="Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-full text-sm" />
                    <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                </div>

                <button onClick={() => setShowDownloadModal(true)} className="p-2.5 bg-green-600 text-white rounded-full hover:bg-green-700" title="Exportar Reporte">
                    <Download size={18} />
                </button>
            </div>

            {/* Mensaje de Periodo Cerrado */}
            {inscripcionCerrada && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700">
                    <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm font-medium">{mensajeCierre}</p>
                </div>
            )}

            <div className="bg-white rounded-lg shadow overflow-x-auto">
                <table className="w-full text-xs">
                    <thead className="bg-gray-50 border-b">
                        <tr>
                            <th colSpan={4} className="border text-center text-[10px] bg-gray-100">IDENTIFICACIÓN</th>
                            <th colSpan={9} className="border text-center text-[10px] bg-gray-100">EVALUACIÓN DE PROCESO</th>
                            <th rowSpan={2} className="border text-center bg-gray-100">
                                <div className="mx-auto" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>PROMEDIO PROCESO</div>
                            </th>
                            <th rowSpan={4} className="border text-center bg-gray-100">
                                <div className="mx-auto text-red-600 font-bold" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>PORCENTAJE ASISTENCIA</div>
                            </th>
                            <th colSpan={3} rowSpan={2} className="border text-center text-[10px] bg-yellow-50">EV. FINAL PERIODO</th>
                            <th rowSpan={2} className="border text-center bg-yellow-100">
                                <div className="mx-auto" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>PROMEDIO FINAL</div>
                            </th>
                        </tr>
                        <tr>
                            <th rowSpan={3} className="border text-center text-[10px] bg-gray-50">No</th>
                            <th rowSpan={3} className="border text-center text-[10px] bg-gray-50">Código</th>
                            <th rowSpan={3} className="border text-center text-[10px] bg-gray-50">Nombre</th>
                            <th rowSpan={3} className="border text-center text-[10px] bg-gray-50">Apellidos</th>
                            {meses.map(m => (
                                <th key={`mhead_${m}`} colSpan={3} className="border text-center text-[10px] bg-gray-50">Mes {m}</th>
                            ))}
                        </tr>
                        <tr>
                            {meses.map(m => camposMes.map(c => (
                                <th key={`f_${m}_${c}`} className="border text-center py-2">
                                    <div className="mx-auto text-[9px]" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>{c === 'Total' ? 'TOTAL' : c === 'P' ? 'PRACTICA 70' : 'TEORIA 30'}</div>
                                </th>
                            )))}
                            <th className="border text-center bg-gray-50 py-2">
                                <div className="mx-auto text-[9px]" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>SUB TOTAL</div>
                            </th>
                            <th className="border text-center bg-yellow-50 py-2">
                                <div className="mx-auto text-[9px] text-red-600 font-bold" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>PRACTICA 70</div>
                            </th>
                            <th className="border text-center bg-yellow-50 py-2">
                                <div className="mx-auto text-[9px]" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>TEORIA 30</div>
                            </th>
                            <th className="border text-center bg-yellow-50 py-2">
                                <div className="mx-auto text-[9px]" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>SUB TOTAL 30%</div>
                            </th>
                            <th className="border text-center bg-yellow-100 py-2">
                                <div className="mx-auto text-[9px] font-bold" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>TOTAL</div>
                            </th>
                        </tr>
                        <tr>
                            {meses.map(m => (
                                <th key={`pts_${m}`} colSpan={3} className="border text-center text-[9px] py-0.5 font-bold bg-gray-50">100 PTS</th>
                            ))}
                            <th className="border text-center text-[9px] py-0.5 font-bold bg-gray-50">70%</th>
                            <th colSpan={3} className="border text-center text-[9px] py-0.5 font-bold bg-yellow-50">30%</th>
                            <th className="border text-center text-[9px] py-0.5 font-bold bg-yellow-100">100%</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y">
                        {loading ? (
                            <tr><td colSpan={19} className="py-8 text-center text-gray-500">Cargando...</td></tr>
                        ) : filteredEstudiantes.length === 0 ? (
                            <tr><td colSpan={19} className="py-8 text-center text-gray-500">
                                {filters.id_paralelo ? 'No hay estudiantes' : 'Seleccione filtros'}
                            </td></tr>
                        ) : filteredEstudiantes.map((est, i) => (
                            <tr key={est.id_estudiante} className="hover:bg-gray-50">
                                <td className="px-2 py-1 border text-center">{i + 1}</td>
                                <td className="px-2 py-1 border text-center">{est.codigo || '-'}</td>
                                <td className="px-2 py-1 border text-center font-medium">{est.nombres || '-'}</td>
                                <td className="px-2 py-1 border text-center font-medium">{est.apellidos || '-'}</td>
                                {meses.map(mes => camposMes.map(campo => (
                                    <td key={`${est.id_estudiante}_${mes}_${campo}`} className="px-1 py-1 border text-center">
                                        {getNota(est.id_estudiante, mes, campo)}
                                    </td>
                                )))}
                                <td className="px-2 py-1 border text-center font-bold bg-gray-50">{getPromProceso(est.id_estudiante)}</td>
                                <td className="px-2 py-1 border text-center text-red-600 font-bold">
                                    {notas[`${est.id_estudiante}_mes0_Asistencia`] || '0%'}
                                </td>
                                <td className="px-2 py-1 border text-center bg-yellow-50/30">
                                    <input type="number" min="0" max="70" value={getNotaPeriodoFinal(est.id_estudiante, 'P')}
                                        onChange={(e) => handleNotaChange(est.id_estudiante, 'P', e.target.value)}
                                        className="w-12 px-1 py-0.5 border border-gray-300 rounded text-center text-xs" />
                                </td>
                                <td className="px-2 py-1 border text-center bg-yellow-50/30">
                                    <input type="number" min="0" max="30" value={getNotaPeriodoFinal(est.id_estudiante, 'T')}
                                        onChange={(e) => handleNotaChange(est.id_estudiante, 'T', e.target.value)}
                                        className="w-12 px-1 py-0.5 border border-gray-300 rounded text-center text-xs" />
                                </td>
                                <td className="px-2 py-1 border text-center font-medium bg-yellow-50 text-xs">
                                    {getSubTotalExamen(est.id_estudiante)}
                                </td>
                                <td className="px-2 py-1 border text-center font-bold bg-yellow-100 text-xs">
                                    {getTotal100(est.id_estudiante)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {filteredEstudiantes.length > 0 && (
                    <div className="p-4 border-t flex justify-end">
                        <button onClick={handleGuardar} disabled={saving}
                            className="px-6 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800 disabled:opacity-50">
                            {saving ? 'Guardando...' : 'Guardar Notas'}
                        </button>
                    </div>
                )}
            </div>

            {/* Modal Descarga - Periodos */}
            {showDownloadModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md animate-in fade-in zoom-in duration-200">
                        <div className="flex justify-between items-center px-6 py-4 border-b">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-800">Descargar Notas de Periodo</h2>
                                <p className="text-xs text-gray-500">Gestión seleccionada: {filters.gestion}</p>
                            </div>
                            <button onClick={() => setShowDownloadModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={24} />
                            </button>
                        </div>
                        <div className="p-6">
                            <label className="block text-sm font-medium mb-4 text-gray-700">Seleccione los periodos a incluir:</label>
                            <div className="grid grid-cols-2 gap-4">
                                {[1, 2, 3, 4].map(p => {
                                    const isSelected = selectedPeriodos.includes(p);
                                    return (
                                        <button key={p} type="button" onClick={() => togglePeriodo(p)}
                                            className={`flex flex-col items-center justify-center p-6 rounded-xl border-2 transition-all ${
                                                isSelected 
                                                ? 'bg-green-50 border-green-600 text-green-700 shadow-sm' 
                                                : 'bg-white border-gray-100 text-gray-600 hover:border-gray-300'
                                            }`}>
                                            <span className="text-sm font-bold">Periodo {p}</span>
                                            <span className="text-[10px] opacity-70 mt-1">
                                                {p === 1 ? 'Mes 1, 2, 3' : p === 2 ? 'Mes 4, 5, 6' : p === 3 ? 'Mes 7, 8, 9' : 'Mes 10, 11, 12'}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="mt-6 flex gap-2">
                                <button type="button" onClick={() => setSelectedPeriodos([1, 2, 3, 4])}
                                    className="text-xs text-green-600 hover:underline">Seleccionar todo</button>
                                <span className="text-gray-300">|</span>
                                <button type="button" onClick={() => setSelectedPeriodos([])}
                                    className="text-xs text-red-600 hover:underline">Limpiar</button>
                            </div>
                        </div>
                        <div className="flex justify-end px-6 py-4 border-t gap-3 bg-gray-50 rounded-b-lg">
                            <button onClick={() => setShowDownloadModal(false)} className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition">
                                Cancelar
                            </button>
                            <button onClick={handleDownload} disabled={selectedPeriodos.length === 0}
                                className="px-6 py-2 bg-green-700 text-white rounded-md font-medium hover:bg-green-800 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-md">
                                Descargar Reporte
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
